/**
 * Composant Relevés Bancaires
 * Gestion de l'import, l'affichage et le rapprochement des transactions bancaires
 */

import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from './Toast';
import CopiloteDeclaration from './CopiloteDeclaration';

const DocumentsFiscaux = ({ clientId }) => {
  const { addToast } = useToast();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  const [exercice, setExercice] = useState(new Date().getFullYear());
  const [showCopilote, setShowCopilote] = useState(false);
  const [copiloteType, setCopiloteType] = useState('IS');
  const [copiloteData, setCopiloteData] = useState(null);

  useEffect(() => {
    loadDocuments();
  }, [clientId, exercice]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      // Récupérer les vrais documents depuis l'API
      const response = await api.get(`/documents/fiscal/list/${clientId}`);

      if (response.success && response.data) {
        // Vérifier que c'est bien un tableau
        const docsArray = Array.isArray(response.data) ? response.data : [];
        // Filtrer par exercice
        const docsFiltered = docsArray.filter(doc => doc.exercice === exercice);
        setDocuments(docsFiltered);
      } else {
        setDocuments([]);
      }
    } catch (err) {
      console.error('Erreur chargement documents:', err);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const generateDocument = async (type) => {
    setGenerating(prev => ({ ...prev, [type]: true }));
    try {
      const response = await api.post(`/documents/generate`, {
        type_doc: type,
        exercice: exercice
      });

      if (response.success) {
        addToast(`Document ${type} généré avec succès !`, 'success');
        loadDocuments();
      } else {
        // Afficher le message d'erreur approprié
        if (response.error === "AUCUNE_DONNEES") {
          addToast(response.message, 'warning');
        } else if (response.error === "LIMITE_ATTEINTE") {
          addToast(response.message, 'error');
        } else {
          addToast(`Erreur: ${response.message}`, 'error');
        }
      }
    } catch (err) {
      addToast('Erreur de connexion au serveur', 'error');
    } finally {
      setGenerating(prev => ({ ...prev, [type]: false }));
    }
  };

  const typesDocuments = [
    { id: 'bilan_simplifie', label: 'Bilan simplifié', description: 'Vue simplifiée du bilan comptable' },
    { id: 'bilan_detaille', label: 'Bilan détaillé', description: 'Bilan complet avec toutes les annexes' },
    { id: 'compte_resultat', label: 'Compte de résultat', description: 'Charges, produits, résultat net' },
    { id: 'liasse_2065', label: 'Liasse fiscale 2065', description: 'Régime IS - Impôt Sociétés' },
    { id: 'declaration_ca3', label: 'Déclaration CA3', description: 'TVA - Déclaration mensuelle/trimestrielle' }
  ];

  if (loading) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px'}}>
        <div style={{textAlign: 'center'}}>
          <div style={{width: '40px', height: '40px', border: '3px solid #1D9E75', borderTopColor: 'transparent', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite'}}></div>
          <p style={{color: '#73726C', fontSize: '14px'}}>Chargement...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      );
  }

  return (
    <div style={{padding: '24px', maxWidth: '1200px', margin: '0 auto'}}>
      {/* Header */}
      <div style={{marginBottom: '32px'}}>
        <h2 style={{fontSize: '24px', fontWeight: '600', color: '#2D2D2D', marginBottom: '8px'}}>
          📄 Documents Fiscaux
        </h2>
        <p style={{fontSize: '14px', color: '#73726C'}}>
          Générez vos déclarations fiscales et bilans en quelques clics
        </p>
      </div>

      {/* Sélecteur d'exercice */}
      <div style={{background: 'white', padding: '16px', borderRadius: '12px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
          <label style={{fontSize: '14px', fontWeight: '500', color: '#2D2D2D'}}>Exercice :</label>
          <select
            value={exercice}
            onChange={(e) => setExercice(parseInt(e.target.value))}
            style={{padding: '8px 12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', flex: 1}}
          >
            {Array.from({ length: 5 }, (_, i) => {
              const year = new Date().getFullYear() - i;
              return <option key={year} value={year}>{year}</option>;
            })}
          </select>
        </div>
      </div>

      {/* Grille des types de documents */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginBottom: '32px'}}>
        {typesDocuments.map((type) => {
          // Compter les documents existants pour ce type
          const countForType = documents.filter(d => d.type_doc === type.id).length;
          const isMaxReached = countForType >= 3;

          return (
            <div
              key={type.id}
              style={{
                background: 'white',
                border: '1px solid #E8E7E3',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                transition: 'all 0.2s',
                opacity: isMaxReached ? 0.7 : 1
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'}
            >
              <div style={{display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px'}}>
                <div style={{width: '40px', height: '40px', borderRadius: '8px', background: isMaxReached ? '#FEE2E2' : '#E1F5EE', color: isMaxReached ? '#D85A30' : '#0F6E56', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px'}}>
                  📄
                </div>
                <div style={{flex: 1}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px'}}>
                    <h3 style={{fontSize: '16px', fontWeight: '600', color: '#2D2D2D'}}>
                      {type.label}
                    </h3>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      color: isMaxReached ? '#D85A30' : countForType > 0 ? '#1D9E75' : '#73726C',
                      background: isMaxReached ? '#FEE2E2' : countForType > 0 ? '#E1F5EE' : '#F3F4F6',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      {countForType}/3
                    </span>
                  </div>
                  <p style={{fontSize: '13px', color: '#73726C', lineHeight: '1.4'}}>
                    {type.description}
                  </p>
                </div>
              </div>

              <button
                onClick={() => generateDocument(type.id)}
                disabled={generating[type.id] || isMaxReached}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: (generating[type.id] || isMaxReached) ? '#73726C' : '#1D9E75',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: (generating[type.id] || isMaxReached) ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => !generating[type.id] && !isMaxReached && (e.target.style.background = '#0F6E56')}
                onMouseLeave={(e) => !generating[type.id] && !isMaxReached && (e.target.style.background = '#1D9E75')}
              >
                {generating[type.id] ? 'Génération...' : isMaxReached ? 'Limite atteinte (3/3)' : 'Générer'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Documents générés */}
      <div>
        <h3 style={{fontSize: '18px', fontWeight: '600', color: '#2D2D2D', marginBottom: '16px'}}>
          Documents générés — {exercice}
        </h3>

        {documents.length === 0 ? (
          <div style={{background: '#FAF9F5', border: '1px dashed #E8E7E3', borderRadius: '12px', padding: '48px', textAlign: 'center'}}>
            <div style={{fontSize: '48px', marginBottom: '12px'}}>📭</div>
            <p style={{fontSize: '14px', color: '#73726C', marginBottom: '16px'}}>
              Aucun document généré pour cet exercice
            </p>
            <p style={{fontSize: '13px', color: '#9CA3AF'}}>
              Sélectionnez un type de document ci-dessus pour le générer
            </p>
          </div>
        ) : (
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px'}}>
            {documents.map((doc) => (
              <div
                key={doc.id}
                style={{
                  background: 'white',
                  border: '1px solid #E8E7E3',
                  borderRadius: '8px',
                  padding: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{width: '32px', height: '32px', borderRadius: '6px', background: doc.statut === 'valide' ? '#1D9E75' : '#F59E0B', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px'}}>
                  {doc.statut === 'valide' ? '✓' : '⚠'}
                </div>
                <div
                  onClick={async () => {
                    try {
                      const response = await api.get(`/documents/fiscal/download/${doc.id}`, {
                        responseType: 'blob'
                      });

                      // Créer un blob et ouvrir dans un nouvel onglet
                      const blob = new Blob([response], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      window.open(url, '_blank');

                      // Libérer l'URL après ouverture
                      setTimeout(() => window.URL.revokeObjectURL(url), 100);
                    } catch (err) {
                      console.error('Erreur téléchargement document:', err);
                      addToast('Erreur lors de l\'ouverture du document', 'error');
                    }
                  }}
                  style={{flex: 1, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '4px'}}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                    e.currentTarget.style.color = '#1D9E75';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                    e.currentTarget.style.color = '#2D2D2D';
                  }}
                >
                  <div style={{fontSize: '13px', fontWeight: '500', color: '#2D2D2D'}}>
                    {doc.type_doc.replace('_', ' ').toUpperCase()}
                  </div>
                  <div style={{fontSize: '11px', color: '#73726C'}}>
                    {new Date(doc.date_generation).toLocaleString('fr-FR')} • {doc.id.substring(0, 8)}
                  </div>
                  <div style={{fontSize: '10px', color: doc.statut === 'valide' ? '#0F6E56' : '#F59E0B', fontWeight: '500'}}>
                    {doc.statut === 'valide' ? 'Validé' : 'Brouillon'}
                  </div>
                </div>
                {['liasse_2065', 'bilan_simplifie', 'bilan_detaille', 'declaration_ca3'].includes(doc.type_doc) && (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const response = await api.get(`/copilote/donnees/${exercice}`);
                        if (response.success) {
                          if (doc.type_doc === 'liasse_2065') {
                            setCopiloteData(response.is_data);
                            setCopiloteType('IS');
                          } else if (doc.type_doc === 'declaration_ca3') {
                            setCopiloteData(response.tva_data);
                            setCopiloteType('TVA');
                          } else {
                            setCopiloteData(response.bilan_data);
                            setCopiloteType('BILAN');
                          }
                          setShowCopilote(true);
                        } else {
                          addToast("Impossible de récupérer les données pour le Copilote.", "error");
                        }
                      } catch (err) {
                        addToast("Erreur lors de la récupération des données de l'exercice.", "error");
                      }
                    }}
                    style={{
                      background: '#E1F5EE',
                      color: '#0F6E56',
                      border: '1px solid #1D9E75',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#1D9E75';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#E1F5EE';
                      e.currentTarget.style.color = '#0F6E56';
                    }}
                    title="Déclarer avec le Copilote"
                  >
                    🤖 Déclarer
                  </button>
                )}
                <button
                  onClick={async (e) => {
                    e.stopPropagation(); // Empêcher le clic de propager au téléchargement
                    if (window.confirm(`Supprimer le document "${doc.type_doc.replace('_', ' ').toUpperCase()}" ?\n\nCette action est irréversible.`)) {
                      try {
                        const response = await api.delete(`/documents/fiscal/${doc.id}`);
                        if (response.success || response.message) {
                          addToast(response.message || 'Document supprimé avec succès', 'success');
                          loadDocuments(); // Recharger la liste
                        } else {
                          addToast('Erreur lors de la suppression', 'error');
                        }
                      } catch (err) {
                        console.error('Erreur suppression document:', err);
                        addToast('Erreur lors de la suppression du document', 'error');
                      }
                    }
                  }}
                  style={{
                    background: '#FEE2E2',
                    border: 'none',
                    borderRadius: '6px',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#FECACA';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#FEE2E2';
                  }}
                  title="Supprimer ce document"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Avertissement légal */}
      <div style={{background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '8px', padding: '12px', marginTop: '24px'}}>
        <div style={{display: 'flex', gap: '8px', fontSize: '12px', color: '#92400E'}}>
          <span>⚠️</span>
          <div>
            <strong style={{color: '#92400E'}}>Document préparatoire</strong> — Ces documents sont générés automatiquement et doivent être validés par votre expert-comptable avant tout envoi à l'administration fiscale.
          </div>
        </div>
      </div>

      {/* Information sur la limite */}
      <div style={{background: '#E1F5EE', border: '1px solid #1D9E75', borderRadius: '8px', padding: '12px', marginTop: '16px'}}>
        <div style={{display: 'flex', gap: '8px', fontSize: '12px', color: '#0F6E56'}}>
          <span>ℹ️</span>
          <div>
            <strong style={{color: '#0F6E56'}}>Limite : 3 documents par type</strong> — Pour générer un nouveau document, supprimez d'abord un des documents existants (icône 🗑️).
          </div>
        </div>
      </div>

      {showCopilote && (
        <CopiloteDeclaration
          clientId={clientId}
          onClose={() => setShowCopilote(false)}
          type={copiloteType}
          data={copiloteData}
        />
      )}
    </div>
  );
};

export default DocumentsFiscaux;
